import { LoadingState } from '../ui';
// src/pages/SquadDetail.jsx
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabaseClient';
import SidebarNavbar from '../components/SidebarNavbar';
import BottomNavbar from '../components/BottomNavbar';
import Modal from '../components/Modal';
import notify from '../lib/notify';
import { FRIENDS_PAGE_SIZE } from '../lib/config';
import { Button as FcButton, Card as FcCard, Input as FcInput } from '../ui';

export default function SquadDetail({ session, userRole }) {
  const { squadId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [squad, setSquad] = useState(null);
  const [members, setMembers] = useState([]);
  const [userSquadRole, setUserSquadRole] = useState(null); // creator, admin, or member
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showLeaveModal, setShowLeaveModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [friends, setFriends] = useState([]);
  const [selectedFriends, setSelectedFriends] = useState([]);
  const [friendSearch, setFriendSearch] = useState('');
  const [friendsHasMore, setFriendsHasMore] = useState(false);
  const [loadingMoreFriends, setLoadingMoreFriends] = useState(false);

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate('/login');
  };

  useEffect(() => {
    if (session?.user?.id && squadId) {
      fetchSquadDetails();
    }
  }, [session?.user?.id, squadId]);

  const fetchSquadDetails = async () => {
    setLoading(true);
    try {
      // Fetch squad info
      const { data: squadData, error: squadError } = await supabase
        .from('squads')
        .select('id, name, description, created_by, created_at')
        .eq('id', squadId)
        .single();

      if (squadError) throw squadError;
      if (!squadData) {
        notify('Squad not found', 'error');
        navigate('/squad');
        return;
      }

      // Fetch creator username
      const { data: creatorData } = await supabase
        .from('profiles')
        .select('username')
        .eq('id', squadData.created_by)
        .single();

      setSquad({
        ...squadData,
        creatorUsername: creatorData?.username || 'Unknown'
      });

      // Fetch squad members
      const { data: membersData, error: membersError } = await supabase
        .from('squad_members')
        .select('id, user_id, role, joined_at')
        .eq('squad_id', squadId)
        .order('joined_at', { ascending: true });

      if (membersError) throw membersError;

      // Fetch member profiles
      const userIds = membersData?.map(m => m.user_id) || [];
      if (userIds.length > 0) {
        const { data: profilesData } = await supabase
          .from('profiles')
          .select('id, username')
          .in('id', userIds);

        const profileMap = {};
        profilesData?.forEach(profile => {
          profileMap[profile.id] = profile.username;
        });

        const membersWithProfiles = membersData.map(member => ({
          ...member,
          username: profileMap[member.user_id] || 'Unknown',
          isCurrentUser: member.user_id === session.user.id
        }));

        setMembers(membersWithProfiles);

        // Set current user's role
        const currentUserMember = membersData.find(m => m.user_id === session.user.id);
        setUserSquadRole(currentUserMember?.role || null);
      }
    } catch (error) {
      notify('Failed to load squad details', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchFriends = async (search = friendSearch) => {
    try {
      // Server-side: friends not already in this squad, searchable + paginated.
      const { data, error } = await supabase.rpc('get_friends_page', {
        p_search: search || null,
        p_limit: FRIENDS_PAGE_SIZE,
        p_offset: 0,
        p_exclude_squad_id: squadId,
      });
      if (error) throw error;
      const page = data || [];
      setFriends(page);
      setFriendsHasMore(page.length === FRIENDS_PAGE_SIZE);
    } catch (error) {
      notify('Failed to load friends', 'error');
    }
  };

  const loadMoreFriends = async () => {
    if (loadingMoreFriends) return;
    setLoadingMoreFriends(true);
    try {
      const { data, error } = await supabase.rpc('get_friends_page', {
        p_search: friendSearch || null,
        p_limit: FRIENDS_PAGE_SIZE,
        p_offset: friends.length,
        p_exclude_squad_id: squadId,
      });
      if (error) throw error;
      const page = data || [];
      setFriends((prev) => [...prev, ...page]);
      setFriendsHasMore(page.length === FRIENDS_PAGE_SIZE);
    } catch (error) {
      notify('Failed to load more friends', 'error');
    } finally {
      setLoadingMoreFriends(false);
    }
  };

  // Debounced search while the invite modal is open.
  useEffect(() => {
    if (!showInviteModal) return;
    const t = setTimeout(() => { fetchFriends(friendSearch); }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [friendSearch, showInviteModal]);

  const handleInviteFriends = async () => {
    if (selectedFriends.length === 0) {
      notify('Please select at least one friend to invite', 'error');
      return;
    }

    try {
      // Add friends as squad members
      const newMembers = selectedFriends.map(friendId => ({
        squad_id: squadId,
        user_id: friendId,
        role: 'member'
      }));

      const { error } = await supabase
        .from('squad_members')
        .insert(newMembers);

      if (error) throw error;

      notify(`Invited ${selectedFriends.length} friend${selectedFriends.length > 1 ? 's' : ''} to the squad!`, 'success');
      setShowInviteModal(false);
      setSelectedFriends([]);
      fetchSquadDetails();
    } catch (error) {
      notify('Failed to invite friends', 'error');
    }
  };

  const handleRemoveMember = async (memberId, memberUsername) => {
    if (!window.confirm(`Remove ${memberUsername} from the squad?`)) {
      return;
    }

    try {
      const { error } = await supabase
        .from('squad_members')
        .delete()
        .eq('id', memberId);

      if (error) throw error;

      notify(`Removed ${memberUsername} from the squad`, 'success');
      fetchSquadDetails();
    } catch (error) {
      notify('Failed to remove member', 'error');
    }
  };

  const handleLeaveSquad = async () => {
    try {
      const { error } = await supabase
        .from('squad_members')
        .delete()
        .eq('squad_id', squadId)
        .eq('user_id', session.user.id);

      if (error) throw error;

      notify('You have left the squad', 'success');
      navigate('/squad');
    } catch (error) {
      notify('Failed to leave squad', 'error');
    }
  };

  const handleDeleteSquad = async () => {
    try {
      const { error } = await supabase
        .from('squads')
        .delete()
        .eq('id', squadId);

      if (error) throw error;

      notify('Squad deleted successfully', 'success');
      navigate('/squad');
    } catch (error) {
      notify('Failed to delete squad', 'error');
    }
  };

  const getRoleBadge = (role) => {
    const badges = {
      creator: 'bg-purple-100 text-purple-800',
      admin: 'bg-blue-100 text-blue-800',
      member: 'bg-gray-100 text-gray-800'
    };
    return badges[role] || badges.member;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-brand-bg">
        <SidebarNavbar handleLogout={handleLogout} userRole={userRole} />
        <BottomNavbar handleLogout={handleLogout} userRole={userRole} />
        <div role="main" id="fc-main-content" className="md:ml-64 pb-20 md:pb-8 fc-workspace">
          <div className="p-4 md:p-8">
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#284239] mx-auto"></div>
              <LoadingState label="Loading squad..." />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!squad) {
    return null;
  }

  return (
    <div className="min-h-screen bg-brand-bg">
      <SidebarNavbar handleLogout={handleLogout} userRole={userRole} />
      <BottomNavbar handleLogout={handleLogout} userRole={userRole} />
      
      <div role="main" id="fc-main-content" className="md:ml-64 pb-20 md:pb-8 fc-workspace">
        <div className="p-4 md:p-8">
          <div className="max-w-4xl mx-auto">
            {/* Header */}
            <FcCard as="div" padding="standard" className="bg-surface-card rounded-lg shadow-sm border border-brand-border p-6 mb-6">
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <h1 className="text-3xl font-bold text-brand-text mb-2">{squad.name}</h1>
                  {squad.description && (
                    <p className="text-content-secondary mb-3">{squad.description}</p>
                  )}
                  <p className="text-sm text-content-secondary">
                    Created by <span className="font-semibold">@{squad.creatorUsername}</span> on{' '}
                    {new Date(squad.created_at).toLocaleDateString('en-GB', {
                      day: 'numeric',
                      month: 'short',
                      year: 'numeric'
                    })}
                  </p>
                  <p className="text-sm text-content-secondary mt-1">
                    {members.length} member{members.length !== 1 ? 's' : ''}
                  </p>
                </div>
                <FcButton variant="tertiary" type="submit" aria-label="Back to squad"
                  onClick={() => navigate('/squad')}
                  className=""
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </FcButton>
              </div>

              {/* Action Buttons */}
              {userSquadRole && (
                <div className="flex gap-2 flex-wrap">
                  {(userSquadRole === 'creator' || userSquadRole === 'admin') && (
                    <FcButton variant="primary" type="submit"
                      onClick={() => {
                        setFriendSearch('');
                        setSelectedFriends([]);
                        setShowInviteModal(true); // the open-modal effect loads the first page
                      }}
                      className=""
                    >
                      👥 Invite Friends
                    </FcButton>
                  )}
                  {userSquadRole !== 'creator' && (
                    <FcButton variant="tertiary" type="submit"
                      onClick={() => setShowLeaveModal(true)}
                      className=""
                    >
                      Leave Squad
                    </FcButton>
                  )}
                  {userSquadRole === 'creator' && (
                    <FcButton variant="destructive" type="submit"
                      onClick={() => setShowDeleteModal(true)}
                      className=""
                    >
                      Delete Squad
                    </FcButton>
                  )}
                </div>
              )}
            </FcCard>

            {/* Members List */}
            <FcCard as="div" padding="standard" className="bg-surface-card rounded-lg shadow-sm border border-brand-border p-6">
              <h2 className="text-xl font-bold text-brand-text mb-4">Squad Members</h2>
              
              {members.length === 0 ? (
                <p className="text-content-secondary text-center py-4">No members yet</p>
              ) : (
                <div className="space-y-3">
                  {members.map((member) => (
                    <div key={member.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-ui-green flex items-center justify-center text-white font-bold">
                          {member.username.charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <p className="font-semibold text-brand-text">
                            @{member.username}
                            {member.isCurrentUser && <span className="text-sm text-content-secondary ml-2">(You)</span>}
                          </p>
                          <p className="text-xs text-content-secondary">
                            Joined {new Date(member.joined_at).toLocaleDateString('en-GB', {
                              day: 'numeric',
                              month: 'short',
                              year: 'numeric'
                            })}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`text-xs px-3 py-1 rounded-full font-semibold ${getRoleBadge(member.role)}`}>
                          {member.role}
                        </span>
                        {(userSquadRole === 'creator' || userSquadRole === 'admin') && 
                         !member.isCurrentUser && 
                         member.role !== 'creator' && (
                          <FcButton variant="destructive" type="submit"
                            onClick={() => handleRemoveMember(member.id, member.username)}
                            className=""
                          >
                            Remove
                          </FcButton>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </FcCard>
          </div>
        </div>
      </div>

      {/* Invite Friends Modal */}
      <Modal title="Squad Detail" isOpen={showInviteModal} onClose={() => setShowInviteModal(false)} maxWidth="max-w-md">
        <div className="p-6">
          <h2 className="text-2xl font-bold text-brand-text mb-4">Invite Friends</h2>

          <FcInput aria-label="Search friends by username…"
            type="text"
            value={friendSearch}
            onChange={(e) => setFriendSearch(e.target.value)}
            placeholder="Search friends by username…"
            className="w-full border mb-4"
          />

          {friends.length === 0 ? (
            <p className="text-content-secondary mb-4">
              {friendSearch
                ? `No friends match “${friendSearch}”.`
                : "No friends available to invite. All your friends are already in this squad or you haven't added any friends yet."}
            </p>
          ) : (
            <>
              <p className="text-sm text-content-secondary mb-4">
                Select friends to add to the squad:
              </p>
              <div className="space-y-2 max-h-96 overflow-y-auto mb-4">
                {friends.map((friend) => (
                  <label key={friend.id} className="flex items-center p-3 bg-gray-50 rounded-lg hover:bg-gray-100 cursor-pointer">
                    <FcInput aria-label="@"
                      type="checkbox"
                      checked={selectedFriends.includes(friend.id)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedFriends([...selectedFriends, friend.id]);
                        } else {
                          setSelectedFriends(selectedFriends.filter(id => id !== friend.id));
                        }
                      }}
                      className="mr-3 w-4"
                    />
                    <span className="font-semibold">@{friend.username}</span>
                  </label>
                ))}
                {friendsHasMore && (
                  <FcButton variant="secondary" type="submit"
                    onClick={loadMoreFriends}
                    disabled={loadingMoreFriends}
                    className="w-full mt-2 border"
                  >
                    {loadingMoreFriends ? 'Loading…' : 'Load more'}
                  </FcButton>
                )}
              </div>
              <FcButton variant="primary" type="submit"
                onClick={handleInviteFriends}
                disabled={selectedFriends.length === 0}
                className="w-full disabled:cursor-not-allowed"
              >
                Invite {selectedFriends.length > 0 ? `(${selectedFriends.length})` : ''}
              </FcButton>
            </>
          )}
          
          <FcButton variant="tertiary" type="submit"
            onClick={() => setShowInviteModal(false)}
            className="w-full mt-2"
          >
            Cancel
          </FcButton>
        </div>
      </Modal>

      {/* Leave Squad Modal */}
      <Modal title="Squad Detail" isOpen={showLeaveModal} onClose={() => setShowLeaveModal(false)} maxWidth="max-w-md">
        <div className="p-6">
          <h2 className="text-2xl font-bold text-brand-text mb-4">Leave Squad</h2>
          <p className="text-content-secondary mb-6">
            Are you sure you want to leave <span className="font-bold">{squad.name}</span>? You'll need to be re-invited to join again.
          </p>
          <div className="flex gap-2">
            <FcButton variant="tertiary" type="submit"
              onClick={handleLeaveSquad}
              className="flex-1"
            >
              Leave Squad
            </FcButton>
            <FcButton variant="tertiary" type="submit"
              onClick={() => setShowLeaveModal(false)}
              className="flex-1"
            >
              Cancel
            </FcButton>
          </div>
        </div>
      </Modal>

      {/* Delete Squad Modal */}
      <Modal title="Squad Detail" isOpen={showDeleteModal} onClose={() => setShowDeleteModal(false)} maxWidth="max-w-md">
        <div className="p-6">
          <h2 className="text-2xl font-bold text-ui-error mb-4">Delete Squad</h2>
          <p className="text-content-secondary mb-6">
            Are you sure you want to delete <span className="font-bold">{squad.name}</span>? This action cannot be undone and all members will be removed.
          </p>
          <div className="flex gap-2">
            <FcButton variant="destructive" type="submit"
              onClick={handleDeleteSquad}
              className="flex-1"
            >
              Delete Squad
            </FcButton>
            <FcButton variant="tertiary" type="submit"
              onClick={() => setShowDeleteModal(false)}
              className="flex-1"
            >
              Cancel
            </FcButton>
          </div>
        </div>
      </Modal>
    </div>
  );
}
